import React, { useState, useEffect, useRef } from "react";
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "firebaseDb";
import Papa from "papaparse";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";

import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Checkbox,
  Divider,
  Card,
} from "@mui/material";

import OrchardOverview from "layouts/maps/components/OrchardOverview";
import OrchardMap from "layouts/maps/components/OrchardMap";
import MapBookPDF from "layouts/maps/components/MapBookPDF";

function Maps() {
  const [orchards, setOrchards] = useState([]);
  const [currentOrchard, setCurrentOrchard] = useState("");
  const [currentBlock, setCurrentBlock] = useState("");
  const [blockInfo, setBlockInfo] = useState([]);
  const [rowInfo, setRowInfo] = useState([]);
  const [loadingdata, setLoading] = useState(false);
  const [markerMode, setMarkerMode] = useState(false);
  const [newMarkerLabel, setNewMarkerLabel] = useState("Marker");
  const [markerIcon, setMarkerIcon] = useState(null);
  const [markerTemplates, setMarkerTemplates] = useState([]);
  const [newMarkerTemplateTitle, setNewMarkerTemplateTitle] = useState("");
  const [markerTemplateError, setMarkerTemplateError] = useState("");
  const [exportError, setExportError] = useState("");
  const [orchardNotes, setOrchardNotes] = useState("");
  const [blockNotes, setBlockNotes] = useState("");
  const [parsedAreas, setParsedAreas] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);

  const fileInputRef = useRef(null);
  const markerTemplateInitializedRef = useRef(false);

  // ✅ Fetch orchards on load
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "OrchardMaps"), (snapshot) => {
      const orchardList = snapshot.docs.map((docu) => ({
        id: docu.id,
        ...docu.data(),
      }));
      setOrchards(orchardList);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "Markers"), (snapshot) => {
      const templates = snapshot.docs.map((docu) => ({
        id: docu.id,
        ...docu.data(),
      }));
      setMarkerTemplates(templates);
    });
    return () => unsub();
  }, []);

  // ✅ Fetch blocks when orchard changes
  useEffect(() => {
    if (!currentOrchard) return () => {};

    const unsub = onSnapshot(
      collection(db, "OrchardMaps", currentOrchard.id, "Blocks"),
      (snapshot) => {
        const blocks = snapshot.docs.map((docu) => ({
          id: docu.id,
          ...docu.data(),
        }));
        setBlockInfo(blocks);
      }
    );

    return () => unsub();
  }, [currentOrchard]);

  // ✅ Fetch rows when block changes
  useEffect(() => {
    if (!currentBlock || !currentOrchard) return () => {};

    const unsub = onSnapshot(
      collection(db, "OrchardMaps", currentOrchard.id, "Blocks", currentBlock.id, "Rows"),
      (snapshot) => {
        const rows = snapshot.docs.map((docu) => ({
          id: docu.id,
          ...docu.data(),
        }));
        setRowInfo(rows);
      }
    );

    return () => unsub();
  }, [currentBlock, currentOrchard]);

  useEffect(() => {
    setOrchardNotes(currentOrchard?.notes || "");
  }, [currentOrchard?.id]);

  useEffect(() => {
    setBlockNotes(currentBlock?.notes || "");
  }, [currentBlock?.id]);

  useEffect(() => {
    if (!markerTemplateInitializedRef.current && markerTemplates.length > 0) {
      setMarkerIcon((current) => current || markerTemplates[0]);
      markerTemplateInitializedRef.current = true;
    }
  }, [markerTemplates]);

  // ✅ Upload CSV to Firestore, with orchard + block auto-create
  const handleCsvUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLoading(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const rows = results.data;

        const writes = rows
          .filter((row) => row["Row Serial #"])
          .map(async (row) => {
            const orchard = row.Orchard;
            const blockId = row["Block ID"];
            const rowSerial = row["Row Serial #"];

            // ✅ ensure orchard exists
            await setDoc(
              doc(db, "OrchardMaps", orchard),
              {
                orchardName: orchard,
              },
              { merge: true }
            );

            // ✅ ensure block exists
            await setDoc(
              doc(db, "OrchardMaps", orchard, "Blocks", blockId),
              {
                orchard,
                blockId,
              },
              { merge: true }
            );

            // ✅ create row
            return setDoc(
              doc(db, "OrchardMaps", orchard, "Blocks", blockId, "Rows", rowSerial),
              {
                Orchard: orchard || "",
                Block: row.Block || "",
                Row: row.Row || "",
                Variety: row.Variety || "",
                TreeCount: Number(row["Tree Count"] || 0),
                DeadTree: Number(row["Dead Tree"] || 0),
                TreeGaps: Number(row["Tree Gaps"] || 0),
                TotalTreeCount: Number(row["Total Tree Count"] || 0),
                OrchardAbbreviation: row["Orchard Abbreviation"] || "",
                BlockID: blockId || "",
                RowID: row["Row ID"] || "",
                RowSerial: rowSerial,
              },
              { merge: true }
            );
          });

        await Promise.all(writes);
        setLoading(false);
        alert("CSV upload complete!");
      },
    });
  };

  const toggleAreaSelection = (id) => {
    setSelectedAreas((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  // toggle the site flag *inside* parsedAreas
  const toggleSiteFlag = (id) => {
    setParsedAreas((prev) =>
      prev.map((area) => (area.id === id ? { ...area, site: !area.site } : area))
    );
  };

  const saveNotes = async () => {
    if (!currentOrchard?.id) return;
    const ref = currentBlock
      ? doc(db, "OrchardMaps", currentOrchard.id, "Blocks", currentBlock.id)
      : doc(db, "OrchardMaps", currentOrchard.id);

    const notes = currentBlock ? blockNotes : orchardNotes;
    await setDoc(ref, { notes }, { merge: true });

    if (currentBlock) {
      setCurrentBlock((prev) => (prev ? { ...prev, notes } : prev));
    } else {
      setCurrentOrchard((prev) => (prev ? { ...prev, notes } : prev));
    }
  };

  const saveMarkerTemplate = async () => {
    const title = newMarkerTemplateTitle.trim();
    const iconUrl = markerIcon?.iconUrl || markerIcon?.url;
    if (!title || !iconUrl) {
      setMarkerTemplateError("Select an icon and add a title first.");
      return;
    }

    try {
      const created = {
        title,
        iconUrl,
        createdAt: new Date().toISOString(),
      };

      await addDoc(collection(db, "Markers"), created);
      setNewMarkerTemplateTitle("");
      setMarkerTemplateError("");
    } catch (error) {
      console.error("Failed to save marker template:", error);
      setMarkerTemplateError("Unable to save marker template right now.");
    }
  };

  const loadMapBookData = async () => {
    if (!currentOrchard?.id) return null;

    const orchardRef = doc(db, "OrchardMaps", currentOrchard.id);
    const orchardSnap = await getDoc(orchardRef);
    const orchardData = orchardSnap.exists()
      ? { id: orchardSnap.id, ...orchardSnap.data() }
      : { ...currentOrchard };

    const blocksSnapshot = await getDocs(
      collection(db, "OrchardMaps", currentOrchard.id, "Blocks")
    );
    const blocks = await Promise.all(
      blocksSnapshot.docs.map(async (blockDoc) => {
        try {
          const rowsSnapshot = await getDocs(
            collection(db, "OrchardMaps", currentOrchard.id, "Blocks", blockDoc.id, "Rows")
          );
          return {
            id: blockDoc.id,
            ...blockDoc.data(),
            rows: rowsSnapshot.docs.map((rowDoc) => ({
              id: rowDoc.id,
              ...rowDoc.data(),
            })),
          };
        } catch (error) {
          console.error(`Failed to fetch rows for block ${blockDoc.id}:`, error);
          return {
            id: blockDoc.id,
            ...blockDoc.data(),
            rows: [],
          };
        }
      })
    );

    return { orchard: orchardData, blocks };
  };

  const handleExportMapBook = async () => {
    try {
      const data = await loadMapBookData();
      if (!data) return;

      const blob = await pdf(<MapBookPDF orchard={data.orchard} blocks={data.blocks} />).toBlob();

      const orchardName = (data.orchard.orchardName || data.orchard.id || "orchard").trim();
      const safeName = orchardName
        .replace(/[\\/:*?"<>|]/g, "-")
        .replace(/^-+|-+$/g, "")
        .replace(/\s+/g, "-");
      saveAs(blob, `${safeName || "orchard"}-map-book.pdf`);
      setExportError("");
    } catch (error) {
      console.error("Failed to export map book:", error);
      setExportError("Unable to export the map book right now.");
    }
  };

  const handleKmlUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, "text/xml");

      const placemarks = xmlDoc.getElementsByTagName("Placemark");

      const blocksData = Array.from(placemarks)
        .map((placemark) => {
          const nameNode = placemark.getElementsByTagName("name")[0];
          const coordsNode = placemark.getElementsByTagName("coordinates")[0];
          if (!nameNode || !coordsNode) return null;

          const blockName = nameNode.textContent.trim();
          const coordsText = coordsNode.textContent.trim();

          const coordinates = coordsText.split(/\s+/).map((pair) => {
            const [lng, lat] = pair.split(",").map(Number);
            return { lat, lng };
          });

          return {
            id: crypto.randomUUID(),
            area: blockName,
            coordinates,
          };
        })
        .filter(Boolean);

      setParsedAreas(blocksData);
      setSelectedAreas([]);
      setLoading(false);

      // ✅ Reset the file input value so it can be reused
      if (fileInputRef.current) fileInputRef.current.value = "";
    };

    reader.readAsText(file);
  };

  const handleImportSelectedAreas = async () => {
    if (selectedAreas.length === 0) {
      alert("No areas selected.");
      return;
    }

    const filtered = parsedAreas
      .filter((area) => selectedAreas.includes(area.id))
      .map((area) => ({
        ...area,
        site: area.site === false, // ensure boolean
      }));
    setLoading(true);

    await setDoc(
      doc(db, "OrchardMaps", currentOrchard.id, "Blocks", currentBlock.id),
      { areas: filtered },
      { merge: true }
    );

    setLoading(false);
    alert("Selected areas imported into Firestore!");
    setParsedAreas([]); // clear UI
    setSelectedAreas([]);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {currentOrchard && (
          <MDBox mb={2}>
            <br />
            <input
              type="file"
              ref={fileInputRef}
              accept=".kml"
              onChange={handleKmlUpload}
              disabled={loadingdata || !currentOrchard} // require an orchard to be selected
            />
            {loadingdata && <p>Uploading file...</p>}
          </MDBox>
        )}

        {parsedAreas.length > 0 && (
          <Card sx={{ mt: 2, p: 2 }}>
            <MDTypography variant="h6" fontWeight="medium" gutterBottom>
              Select Areas to Import
            </MDTypography>

            <List dense sx={{ maxHeight: 260, overflowY: "auto", borderRadius: 2 }}>
              {parsedAreas.map((area) => {
                const labelId = `checkbox-list-label-${area.id}`;
                return (
                  <ListItem
                    key={area.id}
                    disablePadding
                    secondaryAction={
                      <Checkbox
                        checked={area.site === true}
                        onChange={() => toggleSiteFlag(area.id)}
                        inputProps={{ "aria-label": "Mark as site" }}
                      />
                    }
                  >
                    <ListItemButton onClick={() => toggleAreaSelection(area.id)}>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedAreas.includes(area.id)}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ "aria-labelledby": labelId }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        id={labelId}
                        primary={area.area}
                        secondary={area.coordinates.length}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>

            <Divider sx={{ my: 2 }} />

            <MDButton
              variant="contained"
              color="primary"
              disabled={selectedAreas.length === 0}
              onClick={handleImportSelectedAreas}
            >
              Import Selected
            </MDButton>
          </Card>
        )}

        <MDBox mb={2}>
          {!loadingdata && (
            <input type="file" accept=".csv" onChange={handleCsvUpload} disabled={loadingdata} />
          )}
          {" UploadCSV "}
          {loadingdata && <p>Uploading CSV...</p>}
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <OrchardOverview
              orchards={orchards}
              currentOrchard={currentOrchard}
              setCurrentOrchard={setCurrentOrchard}
              currentBlock={currentBlock}
              setCurrentBlock={setCurrentBlock}
              blockInfo={blockInfo}
              markerMode={markerMode}
              setMarkerMode={setMarkerMode}
              newMarkerLabel={newMarkerLabel}
              setNewMarkerLabel={setNewMarkerLabel}
              markerIcon={markerIcon}
              setMarkerIcon={setMarkerIcon}
              markerTemplates={markerTemplates}
              newMarkerTemplateTitle={newMarkerTemplateTitle}
              setNewMarkerTemplateTitle={setNewMarkerTemplateTitle}
              onSaveMarkerTemplate={saveMarkerTemplate}
              markerTemplateError={markerTemplateError}
              orchardNotes={orchardNotes}
              setOrchardNotes={setOrchardNotes}
              blockNotes={blockNotes}
              setBlockNotes={setBlockNotes}
              onSaveNotes={saveNotes}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={8}>
            <OrchardMap
              currentBlock={currentBlock}
              currentOrchard={currentOrchard}
              markerMode={markerMode}
              newMarkerLabel={newMarkerLabel}
              markerIcon={markerIcon}
              blockInfo={blockInfo}
              rowInfo={rowInfo}
            />
            <MDButton sx={{ mt: 2 }} onClick={handleExportMapBook} disabled={!currentOrchard}>
              Export Map Book to PDF
            </MDButton>
            {exportError && (
              <MDTypography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
                {exportError}
              </MDTypography>
            )}
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Maps;
